// src/application.ts
import type { StyleMap, mxConstants, mxGraph } from '@deity-io/mxgraph'; // <- import types only
import mx from 'services/mxGraph/mx'; // <- import values from factory()

export const getDefaultVertexStyle = (graph: mxGraph) => {
  const style = graph.stylesheet.getDefaultVertexStyle();
  style[mx.mxConstants.STYLE_PERIMETER] = mx.mxPerimeter.RectanglePerimeter;
  style[mx.mxConstants.STYLE_SHADOW] = false;
  style[mx.mxConstants.STYLE_VERTICAL_ALIGN] = mx.mxConstants.ALIGN_TOP;
  style[mx.mxConstants.STYLE_ALIGN] = mx.mxConstants.ALIGN_CENTER;
  style[mx.mxConstants.STYLE_RESIZABLE] = '0';
  style[mx.mxConstants.STYLE_WHITE_SPACE] = 'wrap';
  style[mx.mxConstants.STYLE_FOLDABLE] = '0';
  delete style[mx.mxConstants.STYLE_FILLCOLOR];
  delete style[mx.mxConstants.STYLE_STROKECOLOR];

  return style;
};

export const getDefaultEdgeStyle = (graph: mxGraph, style?: mxConstants) => {
  const edgeStyle = graph.stylesheet.getDefaultEdgeStyle();
  edgeStyle[mx.mxConstants.STYLE_ENDARROW] = mx.mxConstants.ARROW_BLOCK;
  edgeStyle[mx.mxConstants.STYLE_ROUNDED] = 1;
  edgeStyle[mx.mxConstants.STYLE_ALIGN] = mx.mxConstants.ALIGN_CENTER;
  edgeStyle[mx.mxConstants.STYLE_VERTICAL_ALIGN] = mx.mxConstants.ALIGN_MIDDLE;
  edgeStyle[mx.mxConstants.STYLE_FONTSIZE] = '10';
  edgeStyle[mx.mxConstants.VALID_COLOR] = '#27bf81';
  edgeStyle[mx.mxConstants.STYLE_STROKECOLOR] = '#bebfd0';
  edgeStyle[mx.mxConstants.STYLE_STROKEWIDTH] = '2';

  edgeStyle[mx.mxConstants.STYLE_EDGE] = style || mx.mxConstants.EDGESTYLE_ENTITY_RELATION;
  edgeStyle[mx.mxConstants.STYLE_BENDABLE] = '1';

  edgeStyle[mx.mxConstants.STYLE_EDITABLE] = '0'; // Does not work
  edgeStyle[mx.mxConstants.STYLE_MOVABLE] = '0'; // Does not work
  edgeStyle[mx.mxConstants.STYLE_RESIZABLE] = '0';

  return edgeStyle;
};

export const getErrorEdgeStyle = (graph: mxGraph, style?: mxConstants) => {
  // const edgeStyle = graph.stylesheet.getDefaultEdgeStyle();
  const edgeStyle: StyleMap = {};
  edgeStyle[mx.mxConstants.STYLE_ENDARROW] = mx.mxConstants.ARROW_BLOCK;
  edgeStyle[mx.mxConstants.STYLE_ROUNDED] = 1;
  edgeStyle[mx.mxConstants.STYLE_ALIGN] = mx.mxConstants.ALIGN_CENTER;
  edgeStyle[mx.mxConstants.STYLE_VERTICAL_ALIGN] = mx.mxConstants.ALIGN_MIDDLE;
  edgeStyle[mx.mxConstants.STYLE_FONTSIZE] = '10';
  edgeStyle[mx.mxConstants.VALID_COLOR] = '#27bf81';
  edgeStyle[mx.mxConstants.STYLE_STROKECOLOR] = 'red';
  edgeStyle[mx.mxConstants.STYLE_STROKEWIDTH] = '2';
  edgeStyle[mx.mxConstants.STYLE_EDGE] = style || mx.mxConstants.EDGESTYLE_ENTITY_RELATION;

  edgeStyle[mx.mxConstants.STYLE_BENDABLE] = '1';

  return edgeStyle;
};

export const getDefaultPortStyle = (
  graph: mxGraph,
  direction?:
    | typeof mx.mxConstants.DIRECTION_NORTH
    | typeof mx.mxConstants.DIRECTION_SOUTH
    | typeof mx.mxConstants.DIRECTION_EAST
    | typeof mx.mxConstants.DIRECTION_WEST
) => {
  const style: StyleMap = {};
  style[mx.mxConstants.STYLE_SHAPE] = mx.mxConstants.SHAPE_IMAGE;
  style[mx.mxConstants.STYLE_FONTCOLOR] = '#774400';
  style[mx.mxConstants.STYLE_PERIMETER] = mx.mxPerimeter.RectanglePerimeter;
  style[mx.mxConstants.STYLE_PERIMETER_SPACING] = '3';
  style[mx.mxConstants.STYLE_ALIGN] = mx.mxConstants.ALIGN_CENTER;
  style[mx.mxConstants.STYLE_VERTICAL_ALIGN] = mx.mxConstants.ALIGN_MIDDLE;
  style[mx.mxConstants.STYLE_FONTSIZE] = '10';
  style[mx.mxConstants.STYLE_FONTSTYLE] = 2;
  if (direction) {
    style[mx.mxConstants.STYLE_PORT_CONSTRAINT] = direction;
    style[mx.mxConstants.STYLE_SOURCE_PORT_CONSTRAINT] = direction;
    style[mx.mxConstants.STYLE_TARGET_PORT_CONSTRAINT] = direction;
  }

  // style[mx.mxConstants.STYLE_IMAGE_WIDTH] = "16";
  // style[mx.mxConstants.STYLE_IMAGE_HEIGHT] = "16";
  return style;
};

export const getPassTroughPortStyle = (graph: mxGraph) => {
  const style: StyleMap = {};
  style[mx.mxConstants.STYLE_SHAPE] = mx.mxConstants.SHAPE_IMAGE;
  style[mx.mxConstants.STYLE_FONTCOLOR] = '#FF44BB';
  style[mx.mxConstants.STYLE_PERIMETER] = mx.mxPerimeter.RectanglePerimeter;
  style[mx.mxConstants.STYLE_PERIMETER_SPACING] = '3';
  style[mx.mxConstants.STYLE_ALIGN] = mx.mxConstants.ALIGN_CENTER;
  // style[mx.mxConstants.STYLE_VERTICAL_ALIGN] = mx.mxConstants.ALIGN_MIDDLE;
  style[mx.mxConstants.STYLE_FONTSIZE] = '10';
  style[mx.mxConstants.STYLE_FONTSTYLE] = 2;
  // Uses string.indexOf to check the constraint, and makes a bitmask for them
  style[mx.mxConstants.STYLE_PORT_CONSTRAINT] = `${mx.mxConstants.DIRECTION_WEST},${mx.mxConstants.DIRECTION_EAST}`;

  return style;
};

export const getGroupVertexStyle = (graph: mxGraph) => {
  const style: StyleMap = {};
  style[mx.mxConstants.STYLE_SHAPE] = mx.mxConstants.SHAPE_SWIMLANE;
  style[mx.mxConstants.STYLE_STARTSIZE] = 30;
  // style[mx.mxConstants.STYLE_SHADOW] = 0;
  style[mx.mxConstants.STYLE_FILLCOLOR] = 'white';
  // style[mx.mxConstants.STYLE_BACKGROUND_OUTLINE] = 0;
  style[mx.mxConstants.STYLE_STROKEWIDTH] = 1;
  style[mx.mxConstants.STYLE_STROKECOLOR] = 'green';
  style[mx.mxConstants.STYLE_RESIZABLE] = true;
  style[mx.mxConstants.STYLE_OPACITY] = 40;
  // style[mx.mxConstants.STYLE_AUTOSIZE] = 0;
  style[mx.mxConstants.STYLE_FOLDABLE] = true;
  // style[mx.mxConstants.STYLE_SWIMLANE_FILLCOLOR] = 'orange';

  return style;
};
